import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiClock, FiMusic } from "react-icons/fi";
import api from "../../utils/api";
import { getSongs } from "../services/songService";

/* duration helper */
const formatDuration = (ms = 0) => {
  const totalSeconds = Math.floor(ms / 1000);
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
};

const HomeFeed = () => {
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [songs, setSongs] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const loadFeed = async () => {
      try {
        const [vRes, sRes, pRes, songRes] = await Promise.all([
          api.get("/client/getVideos", { params: { start: 1, limit: 12 } }),
          api.get("/client/getShorts", { params: { start: 1, limit: 8 } }),
          api.get("/client/posts/feed?limit=8"),
          getSongs(1, 8),
        ]);

        setVideos(vRes?.data?.videos || []);
        setShorts(sRes?.data?.shorts || []);
        setPosts(pRes?.data?.status ? pRes.data.data || [] : []);
        setSongs(songRes?.status ? songRes.songs || [] : []);
      } catch (err) {
        console.error("HomeFeed load error:", err);
      }
    };

    loadFeed();
  }, []);

  const cardStyle = {
    cursor: "pointer",
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  };

  return (
    <div style={{ padding: 20 }}>

      {/* ===== VIDEOS ===== */}
      <Section title="Videos" onViewAll={() => navigate("/videos")}>
        {videos.map((v) => (
          <div
            key={v._id}
            style={cardStyle}
            onClick={() => navigate(`/video/${v._id}`)}
          >
            <Thumbnail src={v.videoImage} duration={v.videoTime} />
            <Meta title={v.title} views={v.viewsCount} />
          </div>
        ))}
      </Section>

      {/* ===== SHORTS ===== */}
      <Section title="Shorts" onViewAll={() => navigate("/shorts")}>
        {shorts.map((s) => (
          <div
            key={s._id}
            style={cardStyle}
            onClick={() => navigate(`/shorts/${s._id}`)}
          >
            <Thumbnail src={s.videoImage} />
            <Meta title="Short" views={s.viewsCount} />
          </div>
        ))}
      </Section>

      {/* ===== SONGS ===== */}
      <Section title="Songs" onViewAll={() => navigate("/songs")}>
        {songs.map((s) => (
          <div
            key={s.slug}
            style={cardStyle}
            onClick={() => navigate(`/song/${s.slug}`)}
          >
            <div style={{ position: "relative" }}>
              <img
                src={s.thumbnailUrl || "/default-song.png"}
                alt={s.title}
                style={{ width: "100%", height: 140, objectFit: "cover" }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  background: "#ff4d67",
                  color: "#fff",
                  fontSize: 11,
                  padding: "2px 6px",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <FiMusic size={12} />
                Song
              </div>
            </div>
            <Meta title={s.title} views={s.totalPlays} />
          </div>
        ))}
      </Section>

      {/* ===== POSTS ===== */}
      <Section title="Posts" onViewAll={() => navigate("/post")}>
        {posts.map((p) => (
          <div key={p._id} style={cardStyle}>
            {p.media?.url && (
              <img
                src={p.media.url}
                alt="post"
                style={{ width: "100%", height: 140, objectFit: "cover" }}
              />
            )}
            <div style={{ padding: 10 }}>
              <div style={{ fontSize: 13 }}>
                {p.caption?.slice(0, 60)}
              </div>
            </div>
          </div>
        ))}
      </Section>

    </div>
  );
};

/* ===== REUSABLE COMPONENTS ===== */

function Section({ title, onViewAll, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <h3>{title}</h3>
        <span
          style={{ cursor: "pointer", color: "#ff4d67" }}
          onClick={onViewAll}
        >
          View all
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 20,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Thumbnail({ src, duration }) {
  return (
    <div style={{ position: "relative" }}>
      <img
        src={src}
        alt="thumbnail"
        style={{ width: "100%", height: 140, objectFit: "cover" }}
      />
      {duration && (
        <div
          style={{
            position: "absolute",
            right: 6,
            bottom: 6,
            background: "rgba(0,0,0,0.75)",
            color: "#fff",
            fontSize: 11,
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {formatDuration(duration)}
        </div>
      )}
    </div>
  );
}

function Meta({ title, views }) {
  return (
    <div style={{ padding: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12, color: "#666" }}>
        {views ?? 0} views
      </div>
    </div>
  );
}

export default HomeFeed;